import React, { useEffect, useState } from "react";
import { db } from "../../firebase";
import {
  collection, getDocs, doc, updateDoc,
  setDoc, deleteDoc, writeBatch
} from "firebase/firestore";
import Modal from "../../components/Modal";
import UISwitch from "../../components/UISwitch";
import { useLanguage } from "../../context/LanguageContext";
import {
  Users, User, Crown, Truck, Monitor, Mail, Phone, UserCheck,
} from "lucide-react";


const ROLE_COLORS = {
  admin:      "bg-blue-100 text-blue-700",
  supervisor: "bg-purple-100 text-purple-700",
  operator:   "bg-orange-100 text-orange-700",
  vendor:     "bg-yellow-100 text-yellow-700",
};

const ROLE_ICONS = {
  admin:      Crown,
  supervisor: UserCheck,
  operator:   Monitor,
  vendor:     Truck,
};

// Helper to render role icon
function RoleIcon({ role, className = "w-3.5 h-3.5" }) {
  const Icon = ROLE_ICONS[role] || User;
  return <Icon className={className} />;
}

function UsersManager({ companyId }) {
  const { t } = useLanguage();
  const [users, setUsers]               = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState("");
  const [success, setSuccess]           = useState("");
  const [saving, setSaving]             = useState(false);
  const [modalOpen, setModalOpen]       = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [filterRole, setFilterRole]     = useState("all");

  // ── Delete state ───────────────────────────────────────────────────────────
  const [deleteModal, setDeleteModal]         = useState(false);
  const [deleteTarget, setDeleteTarget]       = useState(null);
  const [bulkDeleteModal, setBulkDeleteModal] = useState(false);
  const [deleting, setDeleting]               = useState(false);

  // ── Multi-select state ─────────────────────────────────────────────────────
  const [selected, setSelected]     = useState(new Set());
  const [selectMode, setSelectMode] = useState(false);

  const emptyForm = {
    firstName: "", lastName: "", email: "",
    password: "", phone: "", role: "supervisor",
  };
  const [form, setForm] = useState(emptyForm);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadUsers(); }, []);

  async function loadUsers() {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, "companies", companyId, "users"));
      setUsers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      setError("Failed to load users: " + err.message);
    }
    setLoading(false);
  }

  function flash(msg, isError = false) {
    if (isError) setError(msg); else setSuccess(msg);
    setTimeout(() => { setError(""); setSuccess(""); }, 4000);
  }

  // ── Create modal ───────────────────────────────────────────────────────────
  function openCreate() {
    setForm(emptyForm);
    setShowPassword(false);
    setModalOpen(true);
  }

  function closeModal() {
    setModalOpen(false);
    setForm(emptyForm);
    setError("");
  }

  async function handleCreateUser(e) {
    e.preventDefault();
    setError("");
    if (!form.firstName.trim())   return setError("First name is required.");
    if (!form.email.trim())       return setError("Email is required.");
    if (form.password.length < 6) return setError("Password must be at least 6 characters.");

    setSaving(true);
    try {
      // ── Create new user via Firestore trigger ──
      const userId = form.role + "-" + Date.now().toString(36);
      await setDoc(doc(db, "companies", companyId, "users", userId), {
        first_name: form.firstName.trim(),
        last_name:  form.lastName.trim(),
        email:      form.email.trim(),
        password:   form.password.trim(), // Cloud Function reads and deletes this
        phone:      form.phone.trim(),
        role:       form.role,
        companyId:  companyId,
        active:     true,
        created_at: new Date(),
      });

      await loadUsers();
      closeModal();
      flash(`✅ User "${form.firstName} ${form.lastName}" created successfully!`);
    } catch (err) {
      setError("Error: " + err.message);
    }
    setSaving(false);
  }

  // ── Toggle active ──────────────────────────────────────────────────────────
  async function toggleActive(userId, currentValue) {
    try {
      await updateDoc(doc(db, "companies", companyId, "users", userId), {
        active: !currentValue,
      });
      setUsers(prev => prev.map(u =>
        u.id === userId ? { ...u, active: !currentValue } : u
      ));
    } catch (err) {
      flash("Error: " + err.message, true);
    }
  }

  // ── Single delete ──────────────────────────────────────────────────────────
  function askDelete(user) {
    setDeleteTarget(user);
    setDeleteModal(true);
  }

  async function confirmSingleDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteDoc(doc(db, "companies", companyId, "users", deleteTarget.id));
      setUsers(prev => prev.filter(u => u.id !== deleteTarget.id));
      setSelected(prev => { const n = new Set(prev); n.delete(deleteTarget.id); return n; });
      setDeleteModal(false);
      setDeleteTarget(null);
      flash("✅ User deleted.");
    } catch (err) {
      flash("Error: " + err.message, true);
    }
    setDeleting(false);
  }

  // ── Bulk delete ────────────────────────────────────────────────────────────
  async function confirmBulkDelete() {
    if (selected.size === 0) return;
    setDeleting(true);
    try {
      const ids    = Array.from(selected);
      const chunks = [];
      for (let i = 0; i < ids.length; i += 500) chunks.push(ids.slice(i, i + 500));

      for (const chunk of chunks) {
        const batch = writeBatch(db);
        chunk.forEach(id => batch.delete(doc(db, "companies", companyId, "users", id)));
        await batch.commit();
      }

      setUsers(prev => prev.filter(u => !selected.has(u.id)));
      flash(`✅ Deleted ${selected.size} user(s).`);
      setSelected(new Set());
      setSelectMode(false);
      setBulkDeleteModal(false);
    } catch (err) {
      flash("Error: " + err.message, true);
    }
    setDeleting(false);
  }

  // ── Selection helpers ──────────────────────────────────────────────────────
  function toggleSelect(id) {
    setSelected(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  }

  function toggleSelectAll() {
    // Only allow selecting non-admin users
    const selectableIds = filtered.filter(u => u.role !== "admin").map(u => u.id);
    const allSelected   = selectableIds.every(id => selected.has(id)) && selectableIds.length > 0;
    setSelected(allSelected ? new Set() : new Set(selectableIds));
  }

  function exitSelectMode() {
    setSelectMode(false);
    setSelected(new Set());
  }

  // ── Filters ────────────────────────────────────────────────────────────────
  const filtered = users.filter(u =>
    filterRole === "all" ? true : u.role === filterRole
  );

  const roleCounts = users.reduce((acc, u) => {
    acc[u.role] = (acc[u.role] || 0) + 1;
    return acc;
  }, {});

  const selectableFiltered  = filtered.filter(u => u.role !== "admin");
  const allSelectableSelected = selectableFiltered.length > 0 &&
    selectableFiltered.every(u => selected.has(u.id));

  if (loading) return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <p className="text-gray-500 text-sm">{t("users_loading")}</p>
    </div>
  );

  return (
    <div className="space-y-4">

      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl shadow-sm p-4 md:p-6 flex flex-col sm:flex-row justify-between sm:items-center gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">{t("users_title")}</h2>
          <p className="text-gray-500 text-sm mt-1">{users.length} {t("users_total")}</p>
        </div>
        <div className="flex items-center gap-2">
          {!selectMode ? (
            <button onClick={() => setSelectMode(true)}
              className="bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium px-3 py-2 rounded-lg">
              ☑️ {t("users_select")}
            </button>
          ) : (
            <button onClick={exitSelectMode}
              className="bg-gray-200 text-gray-700 text-sm font-medium px-3 py-2 rounded-lg">
              {t("users_cancel")}
            </button>
          )}
          <button onClick={openCreate}
            className="bg-green-600 hover:bg-green-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition">
            {t("users_add")}
          </button>
        </div>
      </div>

      {/* ── Bulk action bar ──────────────────────────────────────────────────── */}
      {selectMode && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <input type="checkbox" checked={allSelectableSelected} onChange={toggleSelectAll}
              className="w-4 h-4 accent-green-600 cursor-pointer" />
            <span className="text-sm text-blue-800 font-medium">
              {selected.size === 0
                ? t("users_select_for_bulk")
                : `${selected.size} ${t("users_selected_count")}`}
            </span>
            {selectMode && (
              <span className="text-xs text-blue-500">{t("users_admins_hint")}</span>
            )}
          </div>
          {selected.size > 0 && (
            <button onClick={() => setBulkDeleteModal(true)}
              className="bg-red-600 hover:bg-red-700 text-white text-sm font-medium px-4 py-2 rounded-lg">
              🗑 {t("users_delete_selected")} ({selected.size})
            </button>
          )}
        </div>
      )}

      {/* ── Feedback ────────────────────────────────────────────────────────── */}
      {error   && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{error}</div>}
      {success && <div className="bg-green-50 border border-green-200 text-green-700 rounded-lg p-3 text-sm">{success}</div>}

      {/* ── Role filter tabs ─────────────────────────────────────────────────── */}
      {users.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm p-4 flex items-center gap-2 flex-wrap">
          {[
            { value: "all",        label: t("users_filter_all"),         count: users.length },
            { value: "admin",      label: t("users_filter_admins"),      count: roleCounts.admin      || 0 },
            { value: "supervisor", label: t("users_filter_supervisors"), count: roleCounts.supervisor || 0 },
            { value: "operator",   label: t("users_filter_operators"),   count: roleCounts.operator   || 0 },
            { value: "vendor",     label: t("users_filter_vendors"),     count: roleCounts.vendor     || 0 },
          ].map(tab => (
            <button key={tab.value} onClick={() => setFilterRole(tab.value)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition flex items-center gap-1.5 ${
                filterRole === tab.value
                  ? "bg-green-600 text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}>
              {tab.label}
              <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${
                filterRole === tab.value ? "bg-green-500 text-white" : "bg-gray-200 text-gray-600"
              }`}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* ── Users List ───────────────────────────────────────────────────────── */}
      {filtered.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center">
          <Users className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-600 font-medium">{t("users_no_users")}</p>
          <p className="text-gray-400 text-sm mt-1">{t("users_no_users_sub")}</p>
          <button onClick={openCreate}
            className="mt-4 bg-green-600 hover:bg-green-700 text-white text-sm font-medium px-4 py-2 rounded-lg">
            {t("users_add")}
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(user => {
            const isAdmin    = user.role === "admin";
            const isSelected = selected.has(user.id);
            return (
              <div key={user.id}
                className={`bg-white rounded-xl shadow-sm p-5 flex justify-between items-center transition ${
                  isSelected ? "ring-2 ring-blue-400 bg-blue-50" : ""
                }`}>
                <div className="flex items-center gap-4">

                  {/* Checkbox — admins cannot be selected */}
                  {selectMode && (
                    <input
                      type="checkbox"
                      checked={isSelected}
                      disabled={isAdmin}
                      onChange={() => !isAdmin && toggleSelect(user.id)}
                      className={`w-4 h-4 flex-shrink-0 ${isAdmin ? "opacity-30 cursor-not-allowed" : "accent-blue-600 cursor-pointer"}`}
                    />
                  )}

                  {/* Avatar */}
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center font-bold text-base ${
                    user.active !== false ? "bg-gray-100 text-gray-600" : "bg-gray-50 text-gray-300"
                  }`}>
                    {user.first_name?.charAt(0).toUpperCase() || user.email?.charAt(0).toUpperCase() || "?"}
                  </div>

                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className={`font-medium ${user.active !== false ? "text-gray-800" : "text-gray-400"}`}>
                        {user.first_name} {user.last_name}
                      </p>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex items-center gap-1 ${ROLE_COLORS[user.role] || "bg-gray-100 text-gray-600"}`}>
                        <RoleIcon role={user.role} /> {t(`role_${user.role}`) || user.role}
                      </span>
                    </div>
                    <p className="text-gray-400 text-xs mt-0.5 flex items-center gap-1"><Mail className="w-3 h-3"/>{user.email}</p>
                    {user.phone && <p className="text-gray-400 text-xs flex items-center gap-1"><Phone className="w-3 h-3"/>{user.phone}</p>}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-3">
                  {isAdmin ? (
                    <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded-lg font-medium">
                      {t("users_admin_badge")}
                    </span>
                  ) : (
                    <>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500">
                          {user.active !== false ? t("users_active") : t("users_disabled")}
                        </span>
                        <UISwitch
                          value={user.active !== false}
                          onChange={() => toggleActive(user.id, user.active !== false)}
                          size="sm"
                        />
                      </div>
                      <button onClick={() => askDelete(user)}
                        className="text-xs px-3 py-1.5 bg-red-50 text-red-600 hover:bg-red-100 rounded-lg font-medium">
                        {t("delete")}
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* ── Single Delete Modal ────────────────────────────────────────────── */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      <Modal
        isOpen={deleteModal}
        onClose={() => { setDeleteModal(false); setDeleteTarget(null); }}
        title={t("users_delete_title")}
        size="sm"
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
              <span className="text-red-600 text-lg">🗑</span>
            </div>
            <div>
              <p className="text-gray-800 font-medium">
                Delete "{deleteTarget?.first_name} {deleteTarget?.last_name}"?
              </p>
              <p className="text-gray-500 text-sm mt-1">
                {t("users_delete_msg")}
              </p>
            </div>
          </div>
          <div className="flex gap-3 pt-2 border-t">
            <button onClick={confirmSingleDelete} disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white text-sm font-medium px-5 py-2 rounded-lg disabled:opacity-50">
              {deleting ? t("deleting") : `🗑 ${t("yes_delete")}`}
            </button>
            <button onClick={() => { setDeleteModal(false); setDeleteTarget(null); }}
              className="bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium px-5 py-2 rounded-lg">
              {t("cancel")}
            </button>
          </div>
        </div>
      </Modal>

      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* ── Bulk Delete Modal ──────────────────────────────────────────────── */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      <Modal
        isOpen={bulkDeleteModal}
        onClose={() => setBulkDeleteModal(false)}
        title={t("users_bulk_delete")}
        size="sm"
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
              <span className="text-lg">⚠️</span>
            </div>
            <div>
              <p className="text-gray-800 font-medium">
                Delete {selected.size} user{selected.size > 1 ? "s" : ""}?
              </p>
              <p className="text-gray-500 text-sm mt-1">
                {t("users_bulk_msg")}
              </p>
            </div>
          </div>

          {/* Preview list */}
          <div className="bg-gray-50 rounded-lg p-3 max-h-40 overflow-y-auto space-y-1">
            {Array.from(selected).map(id => {
              const u = users.find(x => x.id === id);
              if (!u) return null;
              return (
                <p key={id} className="text-xs text-gray-600 flex items-center gap-1.5">
                  <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${ROLE_COLORS[u.role] || "bg-gray-100 text-gray-600"}`}>
                    {t(`role_${u.role}`) || u.role}
                  </span>
                  {u.first_name} {u.last_name} — {u.email}
                </p>
              );
            })}
          </div>

          <div className="flex gap-3 pt-2 border-t">
            <button onClick={confirmBulkDelete} disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white text-sm font-medium px-5 py-2 rounded-lg disabled:opacity-50">
              {deleting ? t("deleting") : `🗑 ${t("delete_all")} (${selected.size})`}
            </button>
            <button onClick={() => setBulkDeleteModal(false)}
              className="bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium px-5 py-2 rounded-lg">
              {t("cancel")}
            </button>
          </div>
        </div>
      </Modal>

      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* ── Create User Modal ──────────────────────────────────────────────── */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      <Modal isOpen={modalOpen} onClose={closeModal} title={t("users_new")} size="md">
        <form onSubmit={handleCreateUser} className="space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{error}</div>
          )}

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-blue-700 text-sm">
              🔐 {t("users_firebase_hint")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("form_first_name")} *</label>
              <input value={form.firstName} onChange={e => setForm(p => ({...p, firstName: e.target.value}))} required
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder={t("form_first_name")} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("form_last_name")}</label>
              <input value={form.lastName} onChange={e => setForm(p => ({...p, lastName: e.target.value}))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder={t("form_last_name")} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("form_email")} * <span className="text-gray-400 font-normal text-xs">{t("form_email_login")}</span>
              </label>
              <input type="email" value={form.email} onChange={e => setForm(p => ({...p, email: e.target.value}))} required
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder="user@company.com" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("form_phone")}</label>
              <input value={form.phone} onChange={e => setForm(p => ({...p, phone: e.target.value}))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder="+971 50 000 0000" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("form_role")} *</label>
              <select value={form.role} onChange={e => setForm(p => ({...p, role: e.target.value}))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500">
                <option value="supervisor">🔭 {t("role_supervisor")}</option>
                <option value="operator">🖥️ {t("role_operator")}</option>
                <option value="vendor">🚗 {t("role_vendor")}</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("form_password")} *</label>
              <div className="relative">
                <input type={showPassword ? "text" : "password"} value={form.password}
                  onChange={e => setForm(p => ({...p, password: e.target.value}))} required minLength={6}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 pr-16"
                  placeholder={t("form_password_min")} />
                <button type="button" onClick={() => setShowPassword(p => !p)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs font-medium">
                  {showPassword ? t("form_hide") : t("form_show")}
                </button>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-500 space-y-1">
            <p>🔭 <strong>{t("role_supervisor")}</strong> — {t("users_role_supervisor_desc").replace(t("role_supervisor") + " — ", "")}</p>
            <p>🖥️ <strong>{t("role_operator")}</strong> — {t("users_role_operator_desc").replace(t("role_operator") + " — ", "")}</p>
            <p>🚗 <strong>{t("role_vendor")}</strong> — {t("users_role_vendor_desc").replace(t("role_vendor") + " — ", "")}</p>
          </div>

          <div className="flex gap-3 pt-2 border-t">
            <button type="submit" disabled={saving}
              className="bg-green-600 hover:bg-green-700 text-white text-sm font-medium px-5 py-2 rounded-lg transition disabled:opacity-50">
              {saving ? t("users_creating") : t("users_create")}
            </button>
            <button type="button" onClick={closeModal}
              className="bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium px-5 py-2 rounded-lg transition">
              {t("cancel")}
            </button>
          </div>
        </form>
      </Modal>

    </div>
  );
}

export default UsersManager;
